/**
 * Summer Sale countdown for the announcement bar.
 *
 * The deadline is per visitor, not per page: the first visit picks a random
 * point between 10 minutes and 3 hours away and writes it to localStorage, so
 * the same clock follows the visitor across every page and reload instead of
 * restarting on each navigation.
 *
 * When it reaches zero the timer is replaced in place by a single "need more
 * time?" control. Taking it adds exactly 10 minutes, once. After that
 * extension runs out the bar settles on a static reserved state and the
 * control does not come back, so the offer cannot be renewed forever.
 */
(() => {
  const STORAGE_KEY = 'h7:summer-sale-timer';
  const STORAGE_VERSION = 1;
  const SECOND = 1000;
  const MINIMUM_WINDOW = 10 * 60 * SECOND;
  const MAXIMUM_WINDOW = 3 * 60 * 60 * SECOND;
  const EXTENSION = 10 * 60 * SECOND;
  // How long a spent deadline is allowed to sit at zero before a fresh window
  // replaces it. Long enough that nobody watching the bar sees it jump, short
  // enough that a visitor coming back later never meets a dead offer.
  const STALE_AFTER = 30 * 60 * SECOND;

  const pad = (value) => String(Math.max(0, Math.floor(value))).padStart(2, '0');

  /**
   * Every storage call is wrapped: Safari in private mode and blocked
   * third-party storage both throw on access, and a countdown must never be
   * the thing that breaks the header.
   */
  const readStoredState = () => {
    let raw = null;
    try {
      raw = window.localStorage.getItem(STORAGE_KEY);
    } catch (error) {
      return null;
    }
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      if (parsed?.version !== STORAGE_VERSION) return null;
      if (!Number.isFinite(parsed.target)) return null;
      // A target further out than the longest window we ever issue means the
      // device clock moved backwards. Start again rather than show a deadline
      // that never arrives.
      if (parsed.target - Date.now() > MAXIMUM_WINDOW) return null;
      return { target: parsed.target, extended: parsed.extended === true };
    } catch (error) {
      return null;
    }
  };

  const writeStoredState = (state) => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ version: STORAGE_VERSION, target: state.target, extended: state.extended })
      );
    } catch (error) {
      // Storage is unavailable, so the timer stays correct for this page view
      // only. That is better than no timer at all.
    }
  };

  const freshState = () => ({
    target: Date.now() + MINIMUM_WINDOW + Math.random() * (MAXIMUM_WINDOW - MINIMUM_WINDOW),
    extended: false,
  });

  /**
   * One deadline, shared by every clock on the page. The announcement bar and
   * the auto-applied notice have to agree to the second, and only one of them
   * carries the extend control, so the state lives here rather than on each
   * element.
   */
  let sharedState = null;
  const instances = new Set();

  const getState = () => {
    if (!sharedState) {
      sharedState = readStoredState();
      if (!sharedState) {
        sharedState = freshState();
        writeStoredState(sharedState);
      }
    }
    return sharedState;
  };

  const setState = (next) => {
    sharedState = next;
    writeStoredState(next);
    instances.forEach((instance) => instance.render());
  };

  /**
   * A deadline that has been spent for longer than STALE_AFTER is replaced by a
   * new window, with the extension available again. Every clock calls this on
   * its own tick, so they all land on the same regenerated deadline within a
   * second without one of them having to drive the others.
   */
  const currentState = () => {
    const state = getState();
    if (state.target > Date.now() - STALE_AFTER) return state;
    sharedState = freshState();
    writeStoredState(sharedState);
    return sharedState;
  };

  if (!customElements.get('h7-sale-countdown')) {
    customElements.define(
      'h7-sale-countdown',
      class H7SaleCountdown extends HTMLElement {
        connectedCallback() {
          if (this.initialized) return;

          this.timer = this.querySelector('[data-h7-sale-timer]');
          this.extend = this.querySelector('[data-h7-sale-extend]');
          this.extendText = this.querySelector('[data-h7-sale-extend-text]');
          this.extendButton = this.querySelector('[data-h7-sale-extend-button]');
          this.hours = this.querySelector('[data-h7-countdown-hours]');
          this.minutes = this.querySelector('[data-h7-countdown-minutes]');
          this.seconds = this.querySelector('[data-h7-countdown-seconds]');

          // The extend control is optional: a read-only clock elsewhere on the
          // page shows the same deadline without offering to move it.
          const required = [this.timer, this.hours, this.minutes, this.seconds];
          if (required.some((element) => !element)) return;

          this.initialized = true;
          this.canExtend = Boolean(this.extend && this.extendText && this.extendButton);
          this.copy = {
            prompt: this.dataset.h7ExtendPrompt || 'Need more time?',
            saved: this.dataset.h7ExtendSaved || 'Saved for 10 minutes',
            final: this.dataset.h7ExtendFinal || 'Offer reserved',
          };
          this.savedUntil = 0;
          instances.add(this);

          if (this.canExtend) {
            this.onExtend = () => this.applyExtension();
            this.extendButton.addEventListener('click', this.onExtend);
          }

          this.render();
          this.intervalId = window.setInterval(() => this.render(), SECOND);
        }

        disconnectedCallback() {
          window.clearInterval(this.intervalId);
          this.extendButton?.removeEventListener('click', this.onExtend);
          instances.delete(this);
          this.initialized = false;
        }

        applyExtension() {
          if (currentState().extended) return;
          // Hold the confirmation on screen briefly so the extension registers
          // as something that happened, then hand back to the running timer.
          this.savedUntil = Date.now() + 2400;
          setState({ target: Date.now() + EXTENSION, extended: true });
        }

        showTimer() {
          this.timer.hidden = false;
          if (!this.canExtend) return;
          this.extend.hidden = true;
          this.extend.classList.remove('h7-announcement-extend--final');
        }

        showExtend(text, isFinal) {
          if (!this.canExtend) {
            // Nothing to offer here, so the clock just sits at zero until the
            // bar's extend control moves the shared deadline.
            this.paintDigits(0);
            return;
          }
          this.timer.hidden = true;
          this.extend.hidden = false;
          this.extend.classList.toggle('h7-announcement-extend--final', isFinal);
          // Hidden rather than only class-styled: the extension is spent, and a
          // button that is still focusable and clickable but does nothing is
          // worse than no button.
          this.extendButton.hidden = isFinal;
          if (this.extendText.textContent.trim() !== text) this.extendText.textContent = text;
        }

        paintDigits(remaining) {
          const totalSeconds = Math.max(0, Math.floor(remaining / SECOND));
          this.hours.textContent = pad(totalSeconds / 3600);
          this.minutes.textContent = pad((totalSeconds % 3600) / 60);
          this.seconds.textContent = pad(totalSeconds % 60);
        }

        render() {
          const now = Date.now();
          const state = currentState();
          const remaining = state.target - now;

          if (this.savedUntil > now) {
            // Paint behind the confirmation too, so the clock is already on the
            // new deadline the moment the confirmation clears.
            this.paintDigits(remaining);
            this.showExtend(this.copy.saved, true);
            return;
          }

          if (remaining <= 0) {
            this.showExtend(state.extended ? this.copy.final : this.copy.prompt, state.extended);
            return;
          }

          this.paintDigits(remaining);
          this.showTimer();
        }
      }
    );
  }
})();
